import React from "react";
import {Box, Typography, Grid, Chip, LinearProgress, Alert, CircularProgress, useTheme, useMediaQuery, Stack} from "@mui/material";
import ContestCard from "./ContestCard";
import { IContestResponse } from "../../../api/types";

interface ContestsListProps {
  contestData: any;
  error: { message: string; type: string } | null;
  loading: any;
  onContinueContest: (contest: any) => void;
  onRegisterContest: (contest: any) => void;
}

const ContestsList = ({
  contestData,
  error,
    loading,
  onContinueContest,
  onRegisterContest,
}: ContestsListProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }
  if (error) {
    return (
      <Box>
          <Typography
              variant="h6"
              sx={{
                  color: "rgb(15, 105, 185)",
                  fontWeight: 500,
                  mb: 3,
              }}
          >
              Leanbot Digital Twin Competition
          </Typography>
          <Alert severity={error.type === 'school' ? 'warning' : 'error'}>
              {error.message}
          </Alert>
      </Box>
    );
  }

  if (
    !contestData ||
    !contestData.contest ||
    !contestData.contest.data ||
    contestData.contest.data.length === 0
  ) {
    return null;
  }

  const {
    contest,
    errorType,
  } = contestData;
  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          color: "rgb(15, 105, 185)",
          fontWeight: 500,
          mb: 3,
          fontSize: { xs: "1.1rem", sm: "1.25rem" },
        }}
      >
        Leanbot Digital Twin Competition
      </Typography>

      {contest && !contest.data ? (
        <Box sx={{ mt: 2 }}>
          {errorType === "school" && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: "1rem", sm: "1.5rem" }, }}>
                It looks like you're not yet associated with a school.
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                - Contact your school administrator to get added.
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                - If you're not part of a school yet, explore our resources and
                learn how to get started!
              </Typography>
            </Box>
          )}

          {errorType === "organization" && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: "1rem", sm: "1.5rem" } }}>
                It looks like your school is not yet associated with a contest
                organization.
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                - Contact Pythaverse administrator to get added.
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <>
          {/* Digital Levels */}
          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
              {contestData.contest.digitalLevel.map((level) => {
                const relatedContest = contestData.contest.data.find(
                  (contest) => contest.level_id === level.id
                );
                const levelProgress = contestData.level_id_data[level.id];

                return (
                  <Box
                    key={level.id}
                    sx={{ display: "flex", alignItems: "center", gap: 2, width: { xs: "100%", sm: "auto" }, }}
                  >
                    <Chip
                      label={level.name}
                      color={
                        relatedContest?.participation_status === 1
                          ? "primary"
                          : "default"
                      }
                      sx={{
                        minWidth: { xs: "30%", sm: 100 },
                        height: 32,
                      }}
                    />
                    {levelProgress && (
                      <Box
                        sx={{
                          flex: { xs: 1, sm: "none" },
                          width: { sm: 100 },
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={levelProgress.completionPercentage || 0}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#DFE3E8",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: "#00B248",
                              boxShadow: "0 0 4px rgba(0, 178, 72, 0.5)",
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Contests List */}
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            Your Competitions
          </Typography>

          <Grid container spacing={3}>
            {contestData.contest.data.map((contest) => {
              const levelProgress = contestData.level_id_data[contest.level_id];
              const isLocked =
                contest.level !== "Beginner" &&
                contestData.progessBeginner < 100;

              return (
                <Grid item xs={12} key={contest.id}>
                  <ContestCard
                    contest={contest}
                    progress={levelProgress?.completionPercentage || 0}
                    lastUpdated={levelProgress?.latestTimeCompleted || "N/A"}
                    onContinue={() => onContinueContest(contest)}
                    onRegister={() => onRegisterContest(contest)}
                    isLocked={isLocked}
                    lockMessage={
                      contest.level === "Intermediate"
                        ? "Complete Beginner level first"
                        : "Complete Intermediate level first"
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ContestsList;
